/**
 * WorkspaceConfigLoader - Loads and instantiates DDD structure from JSON config.
 *
 * This loader handles:
 * 1. Loading initial config from the codebase JSON file (first-time startup)
 * 2. Loading persisted state from localStorage (subsequent startups)
 * 3. Creating Domain and BoundedContext instances from config
 *
 * Priority: localStorage config takes precedence over codebase config.
 * The initial codebase config is only used when no localStorage data exists.
 */

import { DomainImpl, WorkspaceImpl } from '@semantical/modules/semantical-core';
import type { Domain, BoundedContext, Workspace } from '@semantical/modules/semantical-core';
import { OntologyEnabledBoundedContext } from './OntologyEnabledBoundedContext';
import Diagram from '@semantical/modules/diagram/Diagram';
import type {
    WorkspaceConfigRoot,
    DomainConfig,
    BoundedContextConfig,
    ViewConfig
} from './WorkspaceConfig';

// Import the initial config JSON
import initialConfig from '@semantical/config/workspace-config.json';

/**
 * Extract a short ID from a URI/IRI.
 * Examples:
 * - "urn:semantical:domain:party-domain" → "party-domain"
 * - "http://example.org/party#" → "party#"
 */
function extractId(uri: string): string {
    // For URN-style URIs, extract the last segment after ':'
    if (uri.startsWith('urn:')) {
        const parts = uri.split(':');
        return parts[parts.length - 1];
    }
    // For HTTP URIs, extract the fragment or last path segment
    if (uri.includes('#')) {
        return uri.split('#').pop() || uri;
    }
    // Extract last path segment
    const parts = uri.split('/');
    return parts[parts.length - 1] || uri;
}

/** Storage key for workspace configuration */
export const WORKSPACE_CONFIG_STORAGE_KEY = 'semantical.workspaceConfig';

/**
 * Result of loading workspace configuration.
 */
export interface WorkspaceConfigLoadResult {
    /** Root domains loaded from config */
    domains: Domain[];

    /** The raw config for reference */
    config: WorkspaceConfigRoot;

    /** Whether config was loaded from localStorage (true) or initial config (false) */
    fromStorage: boolean;
}

/**
 * Load workspace configuration.
 *
 * Priority:
 * 1. If localStorage has workspace config, use that (user's persisted state)
 * 2. Otherwise, use the initial codebase config (first-time startup)
 *
 * This creates Domain instances with their ontology references.
 * BoundedContexts are referenced but not fully instantiated here
 * (Workspace handles BoundedContext instantiation).
 */
export function loadWorkspaceConfig(): WorkspaceConfigLoadResult {
    // Try to load from localStorage first
    const persistedConfig = loadPersistedConfig();

    if (persistedConfig) {
        console.debug('WorkspaceConfigLoader: Loaded config from localStorage');
        const domains = buildDomainHierarchy(persistedConfig.domains);
        return { domains, config: persistedConfig, fromStorage: true };
    }

    // Fall back to initial codebase config
    console.debug('WorkspaceConfigLoader: No localStorage config found, using initial config');
    const config = initialConfig as WorkspaceConfigRoot;
    const domains = buildDomainHierarchy(config.domains);

    return { domains, config, fromStorage: false };
}

/**
 * Create a Workspace instance from the configuration.
 *
 * This is the primary entry point for initializing the application.
 * It loads the config and creates a WorkspaceImpl with the domain hierarchy.
 *
 * @returns Object containing the workspace and raw config
 */
export function createWorkspace(): {
    workspace: Workspace;
    config: WorkspaceConfigRoot;
    fromStorage: boolean;
} {
    const result = loadWorkspaceConfig();
    const workspace = new WorkspaceImpl({
        id: 'main-workspace',
        name: 'Semantical Workspace',
        version: result.config.version,
        rootDomains: result.domains
    });

    console.debug('WorkspaceConfigLoader: Created workspace with', {
        domainCount: result.domains.length,
        fromStorage: result.fromStorage
    });

    return {
        workspace,
        config: result.config,
        fromStorage: result.fromStorage
    };
}

/**
 * Load the initial workspace configuration from the codebase JSON only.
 * Use this when you explicitly want the default config, ignoring localStorage.
 */
export function loadInitialConfig(): WorkspaceConfigLoadResult {
    const config = initialConfig as WorkspaceConfigRoot;
    const domains = buildDomainHierarchy(config.domains);

    return { domains, config, fromStorage: false };
}

/**
 * Load workspace configuration from localStorage.
 * Returns null if no localStorage data exists.
 */
export function loadPersistedConfig(): WorkspaceConfigRoot | null {
    try {
        const stored = localStorage.getItem(WORKSPACE_CONFIG_STORAGE_KEY);
        if (!stored) {
            return null;
        }
        return JSON.parse(stored) as WorkspaceConfigRoot;
    } catch (error) {
        console.error('Failed to load persisted workspace config:', error);
        return null;
    }
}

/**
 * Save workspace configuration to localStorage.
 */
export function saveWorkspaceConfig(config: WorkspaceConfigRoot): void {
    try {
        localStorage.setItem(WORKSPACE_CONFIG_STORAGE_KEY, JSON.stringify(config));
        console.debug('WorkspaceConfigLoader: Saved config to localStorage');
    } catch (error) {
        console.error('Failed to save workspace config:', error);
    }
}

/**
 * Clear workspace configuration from localStorage.
 * Use this to reset to initial config on next load.
 */
export function clearPersistedConfig(): void {
    try {
        localStorage.removeItem(WORKSPACE_CONFIG_STORAGE_KEY);
        console.debug('WorkspaceConfigLoader: Cleared localStorage config');
    } catch (error) {
        console.error('Failed to clear workspace config:', error);
    }
}

/**
 * Add or update a view reference in a BoundedContext's config.
 * This updates the workspace config in localStorage.
 * Note: View content (classes, relationships, settings) is stored in DiagramRepository,
 * not in WorkspaceConfig - this only tracks which views exist in which BCs.
 */
export function addViewToConfig(
    config: WorkspaceConfigRoot,
    boundedContextId: string,
    viewId: string,
    viewName: string,
    viewType: 'Diagram' | 'Matrix' | 'Table' = 'Diagram'
): void {
    const contextConfig = findBoundedContextConfigById(config, boundedContextId);
    if (!contextConfig) {
        console.error(`Cannot add view: BoundedContext not found: ${boundedContextId}`);
        return;
    }

    // Initialize views array if needed
    if (!contextConfig.views) {
        contextConfig.views = [];
    }

    // Check if view already exists
    const existingIndex = contextConfig.views.findIndex(v => v.id === viewId);
    const viewRef: ViewConfig = {
        id: viewId,
        name: viewName,
        viewType: viewType
    };

    if (existingIndex >= 0) {
        // Update existing - update name and type
        contextConfig.views[existingIndex].name = viewName;
        contextConfig.views[existingIndex].viewType = viewType;
    } else {
        // Add new
        contextConfig.views.push(viewRef);
    }

    // Save updated config
    saveWorkspaceConfig(config);
    console.debug(`WorkspaceConfigLoader: Added/updated view ${viewId} in ${boundedContextId}`);
}

/**
 * Add or update a dataset reference in a BoundedContext's config.
 * This updates the workspace config in localStorage.
 */
export function addDatasetToConfig(
    config: WorkspaceConfigRoot,
    boundedContextId: string,
    datasetId: string,
    datasetName: string,
    datasetType: 'KnowledgeGraph' | 'SPARQLEndpoint' | 'LinkedDataResource' = 'KnowledgeGraph'
): void {
    const contextConfig = findBoundedContextConfigById(config, boundedContextId);
    if (!contextConfig) {
        console.error(`Cannot add dataset: BoundedContext not found: ${boundedContextId}`);
        return;
    }

    // Initialize datasets array if needed
    if (!contextConfig.datasets) {
        contextConfig.datasets = [];
    }

    // Check if dataset already exists
    const existingIndex = contextConfig.datasets.findIndex(d => d.id === datasetId);

    if (existingIndex >= 0) {
        // Update existing - just update the name
        contextConfig.datasets[existingIndex].name = datasetName;
    } else {
        // Add new dataset reference based on type
        if (datasetType === 'KnowledgeGraph') {
            contextConfig.datasets.push({
                type: 'KnowledgeGraph',
                id: datasetId,
                name: datasetName,
                namespace: `urn:semantical:dataset:${datasetId}#`,
                individuals: [],
                relationships: []
            });
        } else if (datasetType === 'SPARQLEndpoint') {
            contextConfig.datasets.push({
                type: 'SPARQLEndpoint',
                id: datasetId,
                name: datasetName,
                endpointUrl: ''
            });
        } else {
            contextConfig.datasets.push({
                type: 'LinkedDataResource',
                id: datasetId,
                name: datasetName,
                resourceUrl: ''
            });
        }
    }

    // Save updated config
    saveWorkspaceConfig(config);
    console.debug(`WorkspaceConfigLoader: Added/updated dataset ${datasetId} in ${boundedContextId}`);
}

/**
 * Remove a dataset reference from a BoundedContext's config.
 */
export function removeDatasetFromConfig(
    config: WorkspaceConfigRoot,
    boundedContextId: string,
    datasetId: string
): void {
    const contextConfig = findBoundedContextConfigById(config, boundedContextId);
    if (!contextConfig || !contextConfig.datasets) {
        return;
    }

    const index = contextConfig.datasets.findIndex(d => d.id === datasetId);
    if (index >= 0) {
        contextConfig.datasets.splice(index, 1);
        saveWorkspaceConfig(config);
        console.debug(`WorkspaceConfigLoader: Removed dataset ${datasetId} from ${boundedContextId}`);
    }
}

/**
 * Remove a view reference from a BoundedContext's config.
 */
export function removeViewFromConfig(
    config: WorkspaceConfigRoot,
    boundedContextId: string,
    viewId: string
): void {
    const contextConfig = findBoundedContextConfigById(config, boundedContextId);
    if (!contextConfig || !contextConfig.views) {
        return;
    }

    const index = contextConfig.views.findIndex(v => v.id === viewId);
    if (index >= 0) {
        contextConfig.views.splice(index, 1);
        saveWorkspaceConfig(config);
        console.debug(`WorkspaceConfigLoader: Removed view ${viewId} from ${boundedContextId}`);
    }
}

/**
 * Find which BoundedContext owns a view by view ID.
 * Searches through all BCs in the config to find the one containing the view.
 * Returns the BC ID if found, undefined otherwise.
 */
export function findContextIdForView(config: WorkspaceConfigRoot, viewId: string): string | undefined {
    function searchInDomain(domainConfig: DomainConfig): string | undefined {
        // Check bounded contexts in this domain
        for (const context of domainConfig.contexts) {
            if (context.views?.some(v => v.id === viewId)) {
                return extractId(context['@id']);
            }
        }
        // Search children recursively
        for (const child of domainConfig.subdomains) {
            const found = searchInDomain(child);
            if (found) return found;
        }
        return undefined;
    }

    for (const domain of config.domains) {
        const found = searchInDomain(domain);
        if (found) return found;
    }
    return undefined;
}

/**
 * Find which BoundedContext contains a vocabulary by vocabulary URI.
 * Searches through all BCs in the config to find one containing the vocabulary.
 * Returns the first matching BC ID if found, undefined otherwise.
 * Note: The same vocabulary URI could exist in multiple BCs.
 */
export function findContextIdForVocabulary(config: WorkspaceConfigRoot, vocabularyUri: string): string | undefined {
    function searchInDomain(domainConfig: DomainConfig): string | undefined {
        // Check bounded contexts in this domain
        for (const context of domainConfig.contexts) {
            const vocabularies = context.hasUbiquitousLanguage?.includesVocabulary ?? [];
            if (vocabularies.some(v => v['@id'] === vocabularyUri)) {
                return extractId(context['@id']);
            }
        }
        // Search children recursively
        for (const child of domainConfig.subdomains) {
            const found = searchInDomain(child);
            if (found) return found;
        }
        return undefined;
    }

    for (const domain of config.domains) {
        const found = searchInDomain(domain);
        if (found) return found;
    }
    return undefined;
}

/**
 * Build Domain hierarchy from config array.
 * Recursively processes children, registers ontologies, and creates
 * OntologyEnabledBoundedContext instances for all bounded contexts.
 *
 * OntologyEnabledBoundedContext construction is lightweight - no ontologies or diagrams are loaded.
 * Heavy initialization happens later when the bounded context becomes active.
 */
function buildDomainHierarchy(
    domainConfigs: DomainConfig[],
    parent?: DomainImpl
): Domain[] {
    return domainConfigs.map(config => {
        const domainUri = config['@id'];
        const domainId = extractId(domainUri);

        const domain = new DomainImpl({
            id: domainId,
            uri: domainUri,
            name: config.name,
            definition: config.definition,
            parent
        });

        // Register ontologies
        for (const ontology of config.ontologies) {
            const ontologyIri = ontology['@id'];
            domain.addOntology({
                id: ontologyIri, // Use IRI as ID
                label: ontology.label,
                namespace: ontologyIri
            });
        }

        // Register with parent
        if (parent) {
            parent.addChild(domain);
        }

        // Create OntologyEnabledBoundedContext instances for all bounded contexts
        // This is lightweight - just identity and model shell, no heavy loading
        for (const contextConfig of config.contexts) {
            const contextUri = contextConfig['@id'];
            const contextId = extractId(contextUri);
            // Filter ontologies by source from merged includesOntology array
            const allOntologies = contextConfig.hasModel.hasSemanticLayer.includesOntology;
            const importedOnts = allOntologies.filter(o => o.source === 'imported');
            const localOnts = allOntologies.filter(o => o.source === 'local');

            // Map config format (@id) to runtime format (iri)
            const importedOntsMapped = importedOnts.map(o => ({ iri: o['@id'], label: o.label }));
            const localOntsMapped = localOnts.map(o => ({ iri: o['@id'], label: o.label }));

            const boundedContext = new OntologyEnabledBoundedContext(
                {}, // No special options needed for non-active bounded contexts
                undefined, // No schema factory needed yet
                {
                    id: contextId,
                    name: contextConfig.name,
                    definition: contextConfig.definition,
                    domain: domain,
                    primaryNamespace: importedOnts[0]?.['@id']
                        ?? localOnts[0]?.['@id']
                        ?? 'http://example.org/default#',
                    importedOntologies: importedOntsMapped,
                    localOntologies: localOntsMapped
                }
            );
            // OntologyEnabledBoundedContext constructor calls domain.addBoundedContext(this)

            // Initialize Ubiquitous Language from config
            if (contextConfig.hasUbiquitousLanguage) {
                const allVocabularies = contextConfig.hasUbiquitousLanguage.includesVocabulary ?? [];
                for (const vocabRef of allVocabularies.filter(v => v.source === 'imported')) {
                    boundedContext.addImportedVocabulary({
                        uri: vocabRef['@id'],
                        label: vocabRef.label,
                        source: 'imported',
                        ttlPath: vocabRef.ttlPath,
                        downloadUrl: vocabRef.downloadUrl
                    });
                }
                for (const vocabRef of allVocabularies.filter(v => v.source === 'local')) {
                    boundedContext.addLocalVocabulary({
                        uri: vocabRef['@id'],
                        label: vocabRef.label,
                        source: 'local',
                        ttlPath: vocabRef.ttlPath,
                        downloadUrl: vocabRef.downloadUrl
                    });
                }
            }

            // Create shell Diagrams from config (content loaded on-demand from DiagramRepository)
            // ViewConfig only contains id, name, viewType - all settings come from DiagramRepository
            if (contextConfig.views) {
                for (const viewConfig of contextConfig.views) {
                    if (viewConfig.viewType === 'Diagram') {
                        const diagram = new Diagram(viewConfig.name, contextId, viewConfig.id);
                        // All diagram settings/content loaded from DiagramRepository when activated
                        boundedContext.registerView(diagram);
                    }
                    // Future: handle Matrix, Table view types
                }
            }

            // Load dataset references from config
            // Note: The actual KG data is loaded lazily by PersistenceService when needed
            if (contextConfig.datasets && contextConfig.datasets.length > 0) {
                console.debug(`WorkspaceConfigLoader: Loading ${contextConfig.datasets.length} datasets for BC ${boundedContext.id}`);
                for (const datasetConfig of contextConfig.datasets) {
                    if (datasetConfig.type === 'KnowledgeGraph') {
                        const datasetRef = {
                            id: datasetConfig.id,
                            name: datasetConfig.name,
                            type: 'KnowledgeGraph' as const
                        };
                        // Just register the reference - actual KG loaded by persistence service
                        boundedContext.setDatasetRef(datasetRef);
                        console.debug(`WorkspaceConfigLoader: Loaded dataset ${datasetRef.name} (${datasetRef.id}) into BC ${boundedContext.id}`);
                    }
                }
                console.debug(`WorkspaceConfigLoader: BC ${boundedContext.id} now has ${boundedContext.getDatasets().length} datasets`);
            }
        }

        // Process children recursively
        if (config.subdomains && config.subdomains.length > 0) {
            buildDomainHierarchy(config.subdomains, domain);
        }

        return domain;
    });
}

/**
 * Find a domain by ID in the hierarchy.
 */
export function findDomainById(domains: Domain[], id: string): Domain | undefined {
    for (const domain of domains) {
        if (domain.id === id) {
            return domain;
        }
        const found = findDomainById(domain.getChildren(), id);
        if (found) {
            return found;
        }
    }
    return undefined;
}

/**
 * Find the domain ID that contains a bounded context with the given ID.
 * Searches the config structure (not runtime Domain hierarchy).
 */
export function findDomainIdForContextId(config: WorkspaceConfigRoot, contextId: string): string | undefined {
    function searchInDomain(domainConfig: DomainConfig): string | undefined {
        // Check if this domain has the context
        for (const context of domainConfig.contexts) {
            if (extractId(context['@id']) === contextId) {
                return extractId(domainConfig['@id']);
            }
        }
        // Search children recursively
        for (const child of domainConfig.subdomains) {
            const found = searchInDomain(child);
            if (found) {
                return found;
            }
        }
        return undefined;
    }

    for (const domain of config.domains) {
        const found = searchInDomain(domain);
        if (found) {
            return found;
        }
    }
    return undefined;
}

/**
 * Find the runtime Domain that should contain a bounded context with the given ID.
 * First finds the domain ID from config, then looks up the runtime Domain.
 */
export function findDomainForContextId(
    domains: Domain[],
    config: WorkspaceConfigRoot,
    contextId: string
): Domain | undefined {
    const domainId = findDomainIdForContextId(config, contextId);
    if (!domainId) {
        return undefined;
    }
    return findDomainById(domains, domainId);
}

/**
 * Find a bounded context config by ID in the config.
 */
export function findBoundedContextConfigById(
    config: WorkspaceConfigRoot,
    contextId: string
): BoundedContextConfig | undefined {
    for (const domain of config.domains) {
        const found = findContextInDomain(domain, contextId);
        if (found) {
            return found;
        }
    }
    return undefined;
}

function findContextInDomain(
    domain: DomainConfig,
    contextId: string
): BoundedContextConfig | undefined {
    for (const context of domain.contexts) {
        if (extractId(context['@id']) === contextId) {
            return context;
        }
    }
    for (const child of domain.subdomains) {
        const found = findContextInDomain(child, contextId);
        if (found) {
            return found;
        }
    }
    return undefined;
}

/**
 * Get all bounded context configs from the configuration.
 */
export function getAllBoundedContextConfigs(
    config: WorkspaceConfigRoot
): BoundedContextConfig[] {
    const contexts: BoundedContextConfig[] = [];

    function collectFromDomain(domain: DomainConfig): void {
        contexts.push(...domain.contexts);
        for (const child of domain.subdomains) {
            collectFromDomain(child);
        }
    }

    for (const domain of config.domains) {
        collectFromDomain(domain);
    }

    return contexts;
}

/**
 * Find a runtime BoundedContext (Workspace) by ID in the domain hierarchy.
 * Returns the actual Workspace instance registered with the domain.
 */
export function findBoundedContextById(domains: Domain[], contextId: string): BoundedContext | undefined {
    for (const domain of domains) {
        // Check bounded contexts in this domain
        for (const context of domain.getBoundedContexts()) {
            if (context.id === contextId) {
                return context;
            }
        }
        // Search children recursively
        const found = findBoundedContextById(domain.getChildren(), contextId);
        if (found) {
            return found;
        }
    }
    return undefined;
}

/**
 * Get the first imported ontology IRI from a bounded context config.
 * Used to determine which ontology to load initially.
 */
export function getPrimaryOntologyIri(context: BoundedContextConfig): string | undefined {
    const allOntologies = context.hasModel.hasSemanticLayer.includesOntology;
    // Prefer imported ontologies first
    const imported = allOntologies.filter(o => o.source === 'imported');
    if (imported.length > 0) {
        return imported[0]['@id'];
    }
    const local = allOntologies.filter(o => o.source === 'local');
    if (local.length > 0) {
        return local[0]['@id'];
    }
    return undefined;
}

/**
 * Find a view config by ID in the workspace config.
 * Returns the view config if found, undefined otherwise.
 */
export function findViewConfigById(
    config: WorkspaceConfigRoot,
    viewId: string
): ViewConfig | undefined {
    function searchInDomain(domainConfig: DomainConfig): ViewConfig | undefined {
        for (const context of domainConfig.contexts) {
            const view = context.views?.find(v => v.id === viewId);
            if (view) {
                return view;
            }
        }
        for (const child of domainConfig.subdomains) {
            const found = searchInDomain(child);
            if (found) return found;
        }
        return undefined;
    }

    for (const domain of config.domains) {
        const found = searchInDomain(domain);
        if (found) return found;
    }
    return undefined;
}
